import axios from 'axios'
import moment from 'moment'
import 'moment/locale/es'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { constants, HOURS_BEFORE_OPEN } from '../Constants'
import { AlertDialogAbandonClass } from '../components/AlertDialogs/AlertDialogAbandonClass'
import { AlertDialogEjectUser } from '../components/AlertDialogs/AlertDialogEjectUser'
import { AlertDialogEnrollUser } from '../components/AlertDialogs/AlertDialogEnrollUser'
import { Button, PageLoader } from '../components/ui'
import { useToast } from '../components/ui/Toast/Toast'
import './EventView.scss'

moment.locale('es')

// Paleta de 24 colores bien diferenciados para avatares [bg, text]
const AVATAR_PALETTE = [
  ['#fde68a', '#92400e'], // amarillo
  ['#fed7aa', '#9a3412'], // naranja
  ['#fca5a5', '#991b1b'], // rojo
  ['#f9a8d4', '#9d174d'], // rosa
  ['#e9d5ff', '#6b21a8'], // violeta
  ['#c7d2fe', '#3730a3'], // índigo
  ['#bfdbfe', '#1e40af'], // azul
  ['#a5f3fc', '#155e75'], // cyan
  ['#99f6e4', '#134e4a'], // teal
  ['#bbf7d0', '#14532d'], // verde
  ['#d9f99d', '#365314'], // lima
  ['#fef08a', '#713f12'], // amarillo limón
  ['#fbcfe8', '#831843'], // rosa oscuro
  ['#ddd6fe', '#4c1d95'], // púrpura
  ['#bae6fd', '#0c4a6e'], // azul cielo
  ['#6ee7b7', '#064e3b'], // esmeralda
  ['#fdba74', '#7c2d12'], // naranja profundo
  ['#f0abfc', '#701a75'], // fucsia
  ['#a78bfa', '#3b0764'], // violeta oscuro
  ['#67e8f9', '#164e63'], // cyan claro
  ['#86efac', '#052e16'], // verde claro
  ['#fde047', '#713f12'], // dorado
  ['#fb7185', '#881337'], // coral
  ['#818cf8', '#1e1b4b'], // azul-violeta
]

// Selecciona un color de la paleta basándose en el hash del nombre
const avatarColor = name => {
  if (!name) return { bg: AVATAR_PALETTE[0][0], text: AVATAR_PALETTE[0][1] }
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  const [bg, text] = AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
  return { bg, text }
}

// Misma interpolación de color que la progress bar de HomeView
const barColorFromPct = pct => {
  const p = Math.min(pct, 100) / 100
  let h, s, l
  if (p <= 0.5) {
    const t = p / 0.5
    h = Math.round(142 + (38 - 142) * t)
    s = Math.round(72 + (92 - 72) * t)
    l = Math.round(45 + (50 - 45) * t)
  } else {
    const t = (p - 0.5) / 0.5
    h = Math.round(38 + (0 - 38) * t)
    s = Math.round(92 + (84 - 92) * t)
    l = Math.round(50 + (55 - 50) * t)
  }
  return `hsl(${h}, ${s}%, ${l}%)`
}

const EventView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const user = JSON.parse(localStorage.getItem('user'))

  const [event, setEvent] = useState(null)
  const [category, setCategory] = useState(null)
  const [dayEvents, setDayEvents] = useState([])
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingParticipants, setLoadingParticipants] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState('')
  const [isInWaitlist, setIsInWaitlist] = useState(false)
  const [twoDaysBefore, setTwoDaysBefore] = useState(false)
  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false)
  const [isInscribing, setIsInscribing] = useState(false)
  const [waitlistUsers, setWaitlistUsers] = useState([])
  const [loadingWaitlist, setLoadingWaitlist] = useState(false)
  const [waitlistOpen, setWaitlistOpen] = useState(false)

  useEffect(() => {
    if (!user?.id) {
      navigate('/login')
      return
    }
  }, [user, navigate])

  const fetchEvent = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(
        `${constants.urlLocal}events/get/${id}/user/${user?.id || ''}`
      )
      if (response.data.ok) {
        setEvent(response.data.event)
        setCategory(response.data.category)
        setDayEvents(response.data.dayEvents || [])
      }
    } catch (error) {
      console.error('Error al obtener el evento:', error)
      // Fallback al endpoint antiguo
      try {
        const fallback = await axios.get(
          `${constants.urlLocal}events/get/${id}`
        )
        setEvent(fallback.data.event)
        setCategory(fallback.data.category)
      } catch (err) {
        console.error('Error fallback:', err)
      }
    } finally {
      setLoading(false)
    }
  }, [id, user?.id])

  const fetchParticipants = useCallback(async () => {
    setLoadingParticipants(true)
    try {
      const response = await axios.get(
        `${constants.urlLocal}inscription/getUsers/${id}/getUsernamesByEvent`
      )
      setParticipants(response.data.users || [])
    } catch (error) {
      console.error('Error al obtener participantes:', error)
    } finally {
      setLoadingParticipants(false)
    }
  }, [id])

  const fetchWaitlist = useCallback(async () => {
    if (!user?.id || !event?._id) return
    try {
      const response = await axios.get(
        `${constants.urlLocal}waitlist/user/${user.id}/event/${event._id}`
      )
      setIsInWaitlist(response.data.ok)
    } catch (error) {
      console.error('Error waitlist:', error)
    }
  }, [user?.id, event?._id])

  const fetchWaitlistUsers = useCallback(async () => {
    if (!event?._id) return
    setLoadingWaitlist(true)
    try {
      const response = await axios.get(
        `${constants.urlLocal}waitlist/event/${event._id}`
      )
      setWaitlistUsers(response.data.waitlistEntries || [])
    } catch (error) {
      console.error('Error obteniendo waitlist:', error)
    } finally {
      setLoadingWaitlist(false)
    }
  }, [event?._id])

  useEffect(() => {
    fetchEvent()
    fetchParticipants()
  }, [fetchEvent, fetchParticipants])
  useEffect(() => {
    fetchWaitlist()
    fetchWaitlistUsers()
  }, [fetchWaitlist, fetchWaitlistUsers])

  // twoDaysBefore reactivo — se recalcula cada minuto
  useEffect(() => {
    if (!event?.start) return
    const check = () =>
      setTwoDaysBefore(
        moment().add(HOURS_BEFORE_OPEN, 'hours').isBefore(moment(event.start))
      )
    check()
    const interval = setInterval(check, 60_000)
    return () => clearInterval(interval)
  }, [event?.start])

  // Countdown timer — solo d/h/m, actualiza cada minuto
  useEffect(() => {
    if (!event?.start) return
    const updateTimer = () => {
      const registrationOpen = moment(event.start).subtract(
        HOURS_BEFORE_OPEN,
        'hours'
      )
      const diff = registrationOpen.diff(moment())
      if (diff > 0) {
        const d = moment.duration(diff)
        const days = Math.floor(d.asDays())
        const hours = d.hours()
        const mins = d.minutes()
        setTimeRemaining(
          days > 0
            ? `${days}d ${hours}h ${String(mins).padStart(2, '0')}m`
            : `${hours}h ${String(mins).padStart(2, '0')}m`
        )
      } else {
        setTimeRemaining('')
      }
    }
    updateTimer()
    const interval = setInterval(updateTimer, 60_000)
    return () => clearInterval(interval)
  }, [event?.start])

  const handleInscribe = async () => {
    if (isInscribing) return
    setIsInscribing(true)
    try {
      await axios.post(`${constants.urlLocal}inscription/create`, {
        userId: user.id,
        eventId: event._id,
      })
      toast({
        title: '¡Te has inscrito! 💪🏽',
        description: 'Ven, entrena y ¡DISFRUTA!',
        status: 'success',
      })
      fetchEvent()
      fetchParticipants()
    } catch (error) {
      toast({
        title: 'Error al inscribirse',
        description: error.response?.data?.message,
        status: 'error',
      })
    } finally {
      setIsInscribing(false)
    }
  }

  const handleUnsubscribe = async () => {
    // Verificar si falta menos de 1 hora para que COMIENCE la clase
    const minutesUntilStart = moment(event.start).diff(moment(), 'minutes') - 50

    if (Math.abs(minutesUntilStart) < 60 && minutesUntilStart !== 0) {
      // Si falta menos de 1h para que inicie, mostrar dialog
      setAbandonDialogOpen(true)
      return
    }

    // Si falta más de 1h, abandonar directamente
    await performUnsubscribe()
  }

  const performUnsubscribe = async () => {
    try {
      await axios.delete(
        `${constants.urlLocal}inscription/delete/${user.id}/${event._id}`
      )
      toast({ title: 'Has abandonado la clase', status: 'warning' })
      setAbandonDialogOpen(false)
      fetchEvent()
      fetchParticipants()
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message,
        status: 'error',
      })
    }
  }

  const handleJoinWaitlist = async () => {
    try {
      await axios.post(`${constants.urlLocal}waitlist/create`, {
        userId: user.id,
        eventId: event._id,
      })
      toast({ title: 'Apuntado a la lista de espera', status: 'success' })
      setIsInWaitlist(true)
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message,
        status: 'error',
      })
    }
  }

  const handleLeaveWaitlist = async () => {
    try {
      await axios.delete(
        `${constants.urlLocal}waitlist/user/${user.id}/event/${event._id}`
      )
      toast({ title: 'Has salido de la lista de espera', status: 'info' })
      setIsInWaitlist(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message,
        status: 'error',
      })
    }
  }

  const handleEjectUser = async userId => {
    try {
      await axios.delete(
        `${constants.urlLocal}inscription/delete/${userId}/${event._id}`
      )
      toast({ title: 'Usuario expulsado', status: 'warning' })
      fetchParticipants()
    } catch (error) {
      toast({
        title: 'Error al expulsar',
        description: error.response?.data?.message,
        status: 'error',
      })
    }
  }

  if (loading) return <PageLoader text="Cargando clase..." />
  if (!event)
    return (
      <div className="page">
        <p>Clase no encontrada</p>
      </div>
    )

  const isEnrolled = participants.some(p => p.id === user?.id)
  const isFull = participants.length >= 24
  const eventHasPassed = moment().isAfter(moment(event.start))

  return (
    <div className="event-view page">
      {/* Back */}
      <button className="event-view__back" onClick={() => navigate(-1)}>
        ← Volver
      </button>

      {/* Hero */}
      <div
        className="event-view__hero"
        style={{ '--accent': category?.color || event.color }}
      >
        <span className="event-view__hero-date">
          {moment(event.start).utc().format('dddd, DD MMM')}
        </span>
        <h1 className="event-view__hero-title">{event.title}</h1>
        <div className="event-view__hero-time">
          <span className="event-view__hero-hour">
            {moment(event.start).utc().format('HH:mm')}
          </span>
          <span className="event-view__hero-separator">—</span>
          <span className="event-view__hero-hour">
            {moment(event.end).utc().format('HH:mm')}
          </span>
        </div>
        <div className="event-view__hero-meta">
          <div
            className="event-view__hero-spots"
            style={{
              '--spots-color':
                participants.length >= 22
                  ? '#ef4444'
                  : participants.length >= 16
                    ? '#f59e0b'
                    : '#10b981',
              '--spots-fill': `${Math.round((participants.length / 24) * 100)}%`,
            }}
          >
            <div className="event-view__hero-spots-numbers">
              <span className="event-view__hero-spots-count">
                {participants.length}
              </span>
              <span className="event-view__hero-spots-total">/24</span>
            </div>
            <div className="event-view__hero-spots-bar">
              <div className="event-view__hero-spots-bar-fill" />
            </div>
            <span className="event-view__hero-spots-label">
              {24 - participants.length > 0
                ? `${24 - participants.length} plazas libres`
                : 'Clase completa'}
            </span>
          </div>
        </div>
      </div>

      {/* Status */}
      {isFull && !eventHasPassed && (
        <div className="event-view__status event-view__status--full">
          Clase completa
        </div>
      )}
      {eventHasPassed && (
        <div className="event-view__status event-view__status--passed">
          Esta clase ya ha terminado
        </div>
      )}

      {/* Action */}
      {!eventHasPassed && category?.isSingleEnrollment !== false && (
        <div className="event-view__actions">
          {isEnrolled ? (
            <Button variant="danger" fullWidth onClick={handleUnsubscribe}>
              Abandonar clase
            </Button>
          ) : isFull && !isInWaitlist ? (
            <Button variant="warning" fullWidth onClick={handleJoinWaitlist}>
              Apuntarse a la espera
            </Button>
          ) : isInWaitlist ? (
            <Button variant="primary" fullWidth onClick={handleLeaveWaitlist}>
              Salir de la cola
            </Button>
          ) : (
            <Button
              variant="primary"
              fullWidth
              onClick={handleInscribe}
              disabled={twoDaysBefore || isInscribing}
            >
              {twoDaysBefore
                ? `Abre en ${timeRemaining || '...'}`
                : isInscribing
                  ? 'Inscribiendo...'
                  : 'Inscribirse'}
            </Button>
          )}
        </div>
      )}

      {/* Day siblings */}
      {dayEvents.length > 0 && (
        <div className="event-view__day-nav">
          <span className="event-view__section-label">Más clases hoy</span>
          <div className="event-view__day-events">
            {dayEvents.map(dayEvent => {
              const isCurrent = dayEvent._id === id
              const pct = Math.round((dayEvent.inscriptionCount / 24) * 100)
              const fillColor = barColorFromPct(pct)
              return (
                <Link
                  key={dayEvent._id}
                  to={`/event/${dayEvent._id}`}
                  className={`event-view__day-event${isCurrent ? ' event-view__day-event--active' : ''}`}
                  style={{
                    '--accent': dayEvent.categoryColor || dayEvent.color,
                    '--fill-color': fillColor,
                  }}
                >
                  <span className="event-view__day-event-time">
                    {moment(dayEvent.start).utc().format('HH:mm')}
                  </span>
                  <span className="event-view__day-event-name">
                    {dayEvent.title}
                  </span>
                  <span
                    className="event-view__day-event-count"
                    style={{ color: fillColor }}
                  >
                    {dayEvent.inscriptionCount}/24
                  </span>
                  <span
                    className="event-view__day-event-fill"
                    style={{ '--pct': `${pct}%` }}
                  />
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Waitlist */}
      {(user?.role === 'admin' || isInWaitlist) && (
        <div
          className={`event-view__waitlist${waitlistOpen ? ' event-view__waitlist--open' : ''}`}
        >
          <button
            className="event-view__waitlist-toggle"
            onClick={() => setWaitlistOpen(o => !o)}
          >
            <span className="event-view__waitlist-toggle-left">
              <span className="event-view__waitlist-icon">⏳</span>
              <span className="event-view__section-label">Lista de espera</span>
              <span className="event-view__waitlist-badge">
                {loadingWaitlist ? '…' : waitlistUsers.length}
              </span>
            </span>
            <span className="event-view__waitlist-chevron">
              {waitlistOpen ? '▲' : '▼'}
            </span>
          </button>

          {waitlistOpen && (
            <div className="event-view__waitlist-body">
              {loadingWaitlist ? (
                <div className="waitlist-list">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="waitlist-row waitlist-row--skeleton"
                    />
                  ))}
                </div>
              ) : waitlistUsers.length === 0 ? (
                <div className="event-view__empty">
                  <span className="event-view__empty-icon">⏳</span>
                  <p>Nadie en lista de espera</p>
                </div>
              ) : (
                <div className="waitlist-list">
                  {waitlistUsers.map((entry, idx) => {
                    const name = entry.user?.name || ''
                    const surname = entry.user?.lastname || ''
                    const c = avatarColor(`${name}${surname}`)
                    return (
                      <div
                        key={entry.user?._id || idx}
                        className="waitlist-row"
                        style={{ animationDelay: `${idx * 0.03}s` }}
                      >
                        <span className="waitlist-row__pos">{idx + 1}</span>
                        <div
                          className="waitlist-row__avatar"
                          style={{ background: c.bg, '--avatar-text': c.text }}
                        >
                          <span style={{ color: `var(--avatar-text)` }}>
                            {name[0]}
                            {surname[0]}
                          </span>
                        </div>
                        <span className="waitlist-row__name">
                          {name} {surname}
                        </span>
                        {entry.user?._id === user?.id && (
                          <span className="waitlist-row__you">Tú</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Participants */}
      <div className="event-view__participants">
        <div className="event-view__participants-header">
          <span className="event-view__section-label">Participantes</span>
          {user?.role === 'admin' && (
            <AlertDialogEnrollUser
              eventId={id}
              participants={participants}
              onSuccess={fetchParticipants}
            />
          )}
        </div>

        {loadingParticipants ? (
          <div className="participant-list">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="participant-row participant-row--skeleton"
              />
            ))}
          </div>
        ) : participants.length === 0 ? (
          <div className="event-view__empty">
            <span className="event-view__empty-icon">🏃</span>
            <p>Aún no hay nadie apuntado</p>
            <span>¡Sé el primero en inscribirte!</span>
          </div>
        ) : (
          <div className="participant-grid">
            {participants.map((participant, idx) => {
              const c = avatarColor(`${participant.name}${participant.surname}`)
              const fullName =
                `${participant.name || ''} ${participant.surname || ''}`.trim()
              const isAdmin = user?.role === 'admin'
              const canEject = isAdmin && category?.isSingleEnrollment

              const avatarEl = (
                <div
                  className={`participant-bubble__avatar${canEject ? ' participant-bubble__avatar--ejectable' : ''}`}
                  style={
                    !participant.avatar
                      ? { background: c.bg, '--avatar-text': c.text }
                      : undefined
                  }
                >
                  {participant.avatar ? (
                    <img
                      src={participant.avatar}
                      alt={fullName || participant.name}
                    />
                  ) : (
                    <span style={{ color: `var(--avatar-text)` }}>
                      {participant.name?.[0]}
                      {participant.surname?.[0]}
                    </span>
                  )}
                </div>
              )

              return (
                <div
                  key={participant.id || idx}
                  className={`participant-bubble${isAdmin && !participant.hasPaid ? ' participant-bubble--unpaid' : ''}`}
                  style={{ animationDelay: `${idx * 0.03}s` }}
                >
                  {canEject ? (
                    <AlertDialogEjectUser
                      participant={participant}
                      onConfirm={handleEjectUser}
                      trigger={avatarEl}
                    />
                  ) : (
                    avatarEl
                  )}
                  <span className="participant-bubble__name">{fullName}</span>
                  {isAdmin && !participant.hasPaid && (
                    <span className="participant-bubble__unpaid">Sin pago</span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Dialog para ceder puesto si abandona menos de 1h antes */}
      <AlertDialogAbandonClass
        isOpen={abandonDialogOpen}
        onClose={() => setAbandonDialogOpen(false)}
        onConfirm={performUnsubscribe}
        eventTitle={event?.title || ''}
        eventTime={event ? moment(event.start).utc().format('HH:mm') : ''}
      />
    </div>
  )
}

export default EventView
